import { formatNumber, formatFixed } from '../../../shared/utils/numberLocale';
import { getPersianDateFromGregorian } from '../../../shared/utils/persianDate';
import { cn } from '../../../lib/utils';

/**
 * Normalize column/stat format options.
 * Supports legacy string formats ('number'|'percent'|'currency'|'text')
 * and rich objects: { type, decimals, useGrouping, align, bold, italic }.
 */
function defaultDecimalsForType(type) {
  if (type === 'text') return 0;
  return 2;
}

export function normalizeFormat(format) {
  if (format == null || format === '') {
    return {
      type: 'text',
      decimals: 2,
      useGrouping: true,
      align: 'right',
      bold: false,
      italic: false,
      currencySuffix: 'rial',
    };
  }
  if (typeof format === 'string') {
    return {
      type: format,
      decimals: defaultDecimalsForType(format),
      useGrouping: format !== 'percent',
      align: 'right',
      bold: false,
      italic: false,
      currencySuffix: format === 'currency' ? 'rial' : 'none',
    };
  }
  const type = format.type || format.format || 'number';
  return {
    type,
    decimals: format.decimals != null ? Number(format.decimals) : defaultDecimalsForType(type),
    useGrouping: format.useGrouping != null ? !!format.useGrouping : type !== 'percent',
    align: format.align || 'right',
    bold: !!format.bold,
    italic: !!format.italic,
    currencySuffix: format.currencySuffix || (type === 'currency' ? 'rial' : 'none'),
  };
}

/** Merge top-level style fields (align/bold/italic/decimals) with format. */
export function resolveDisplayFormat(colOrStat = {}) {
  const base = normalizeFormat(colOrStat.format);
  const type = colOrStat.format && typeof colOrStat.format === 'string'
    ? colOrStat.format
    : (base.type || 'number');
  return {
    type,
    decimals: colOrStat.decimals != null ? Number(colOrStat.decimals) : base.decimals,
    useGrouping: colOrStat.useGrouping != null ? !!colOrStat.useGrouping : base.useGrouping,
    align: colOrStat.align || base.align,
    bold: colOrStat.bold != null ? !!colOrStat.bold : base.bold,
    italic: colOrStat.italic != null ? !!colOrStat.italic : base.italic,
    currencySuffix: colOrStat.currencySuffix || base.currencySuffix || (type === 'currency' ? 'rial' : 'none'),
  };
}

function resolveDecimals(fmt) {
  const n = Number(fmt.decimals);
  if (!Number.isFinite(n)) return fmt.type === 'text' ? 0 : 2;
  return Math.max(0, Math.min(6, n));
}

export function formatReportValue(value, colOrStat = {}) {
  if (value == null || value === '') return '—';
  const fmt = resolveDisplayFormat(colOrStat);
  const decimals = resolveDecimals(fmt);

  if (fmt.type === 'text') return String(value);

  if (fmt.type === 'percent') {
    return `${formatFixed(value, decimals)}%`;
  }

  const opts = {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    useGrouping: fmt.useGrouping !== false,
  };

  const formatted = formatNumber(value, opts);
  if (fmt.type === 'currency') {
    const suffix = fmt.currencySuffix || 'rial';
    if (suffix === 'none') return formatted;
    if (suffix === 'sign') return `${formatted} ﷼`;
    return `${formatted} ریال`;
  }

  return formatted;
}

export function formatChartNumber(value, decimals = 0) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const places = Math.max(0, Math.min(6, Number(decimals) || 0));
  return formatNumber(n, {
    maximumFractionDigits: places,
    minimumFractionDigits: places,
  });
}

const ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function styleClassNames(colOrStat = {}, extra = '') {
  const fmt = resolveDisplayFormat(colOrStat);
  return cn(
    ALIGN_CLASS[fmt.align] || 'text-right',
    fmt.bold && 'font-bold',
    fmt.italic && 'italic',
    extra,
  );
}

/**
 * Client-side cell transform (safe presets + limited custom JS using `value`).
 * Presets: round:N | abs | percent | date:fa | date:fa:pattern | custom expression
 * Date patterns: yyyy MM dd HH mm ss  e.g. yyyy-MM-dd HH:mm
 */
function pad2(n) {
  return String(n).padStart(2, '0');
}

export function formatPersianDateTime(value, pattern = 'yyyy/MM/dd') {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = getPersianDateFromGregorian(d);
  const tokens = {
    yyyy: String(p.year),
    MM: pad2(p.month),
    dd: pad2(p.day),
    HH: pad2(d.getHours()),
    mm: pad2(d.getMinutes()),
    ss: pad2(d.getSeconds()),
  };
  return String(pattern || 'yyyy/MM/dd').replace(/yyyy|MM|dd|HH|mm|ss/g, (tok) => tokens[tok] ?? tok);
}

export function isDateFaFormula(formula) {
  const f = String(formula || '').trim();
  return f === 'date:fa' || f.startsWith('date:fa:');
}

export function applyFieldFormula(raw, formula, row) {
  if (formula == null || formula === '') return raw;
  const f = String(formula).trim();
  const hasValue = raw != null && raw !== '';

  if (f.startsWith('round:')) {
    if (!hasValue) return raw;
    const d = Math.max(0, Math.min(6, Number(f.slice(6)) || 0));
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    const fct = 10 ** d;
    return Math.round(n * fct) / fct;
  }
  if (f === 'abs') {
    if (!hasValue) return raw;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.abs(n) : raw;
  }
  if (f === 'percent') {
    if (!hasValue) return raw;
    const n = Number(raw);
    return Number.isFinite(n) ? n * 100 : raw;
  }
  if (f === 'date:fa' || f.startsWith('date:fa:')) {
    if (!hasValue) return raw;
    try {
      const d = raw instanceof Date ? raw : new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      const pattern = f === 'date:fa' ? '' : f.slice('date:fa:'.length).trim();
      if (!pattern) {
        return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(d);
      }
      return formatPersianDateTime(d, pattern);
    } catch {
      return String(raw);
    }
  }

  // Custom: expression may use `value`, `row`, Math, Number, String, Date
  if (/[;{}`]|process|require|window|document|global|Function|eval|import/i.test(f)) {
    return raw;
  }
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(
      'value',
      'row',
      'Math',
      'Number',
      'String',
      'Date',
      `"use strict"; return (${f});`,
    );
    return fn(raw, row || {}, Math, Number, String, Date);
  } catch {
    return raw;
  }
}

export function formatReportCell(raw, colOrStat = {}, row) {
  const transformed = applyFieldFormula(raw, colOrStat.formula, row);
  return formatReportValue(transformed, colOrStat);
}

/** Config envelope helpers (backward compatible with bare reports array). */
export const BUILTIN_CUSTOMER_TARGET_ID = '__builtin_customer_target__';

export function parseReportsConfig(raw) {
  if (Array.isArray(raw)) {
    const flagged = raw.find((r) => r.is_main_dashboard)?.id || null;
    return {
      showCustomerTarget: false,
      defaultReportId: flagged || raw[0]?.id || null,
      useCustomAsMainDashboard: true,
      mainDashboardReportId: flagged || raw[0]?.id || null,
      reports: raw,
    };
  }
  if (raw && typeof raw === 'object') {
    const reports = Array.isArray(raw.reports) ? raw.reports : [];
    const flagged = reports.find((r) => r.is_main_dashboard)?.id || null;
    const firstId = reports[0]?.id || null;
    return {
      showCustomerTarget: false,
      defaultReportId: raw.defaultReportId && raw.defaultReportId !== BUILTIN_CUSTOMER_TARGET_ID
        ? raw.defaultReportId
        : (flagged || firstId),
      useCustomAsMainDashboard: raw.useCustomAsMainDashboard !== false,
      mainDashboardReportId: (
        raw.mainDashboardReportId && raw.mainDashboardReportId !== BUILTIN_CUSTOMER_TARGET_ID
          ? raw.mainDashboardReportId
          : (flagged || firstId)
      ),
      reports,
    };
  }
  return {
    showCustomerTarget: false,
    defaultReportId: null,
    useCustomAsMainDashboard: true,
    mainDashboardReportId: null,
    reports: [],
  };
}

export function serializeReportsConfig({
  showCustomerTarget,
  defaultReportId,
  useCustomAsMainDashboard,
  mainDashboardReportId,
  reports,
}) {
  return {
    showCustomerTarget: showCustomerTarget !== false,
    defaultReportId: defaultReportId || null,
    useCustomAsMainDashboard: useCustomAsMainDashboard === true,
    mainDashboardReportId: mainDashboardReportId || null,
    reports: reports || [],
  };
}

/** Resolve which custom report should render on home `/`. */
export function resolveMainDashboardReport(config) {
  const reports = (config?.reports || []).filter((r) => r && r.visible !== false);
  if (!reports.length) return null;

  const id = config.mainDashboardReportId;
  if (id && id !== BUILTIN_CUSTOMER_TARGET_ID) {
    const match = reports.find((r) => r.id === id);
    if (match) return match;
  }
  const designed = reports.find((r) => r.id === 'dashboard_home');
  if (designed) return designed;
  const flagged = reports.find((r) => r.is_main_dashboard);
  if (flagged) return flagged;
  if (config.useCustomAsMainDashboard) return reports[0];
  return null;
}
